import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PricePreviewResponse, Totals } from '@paddle/paddle-js';
import { getPaddle, isPaddleConfigured } from '@/features/billing/paddle';
import { billingKeys } from '@/features/billing/queries';
import { isPurchasable } from '@/features/billing/plan-helpers';
import type { BillingPlan } from '@/lib/billing';

/**
 * Location-based pricing — the ONE place Paddle's geo decision enters the app.
 *
 * `Paddle.PricePreview` resolves the visitor's currency SERVER-SIDE from the
 * request that carries their IP: we never see an IP, never send one, and never
 * convert a price ourselves. An Indian visitor and a US visitor get different
 * numbers out of the same build because Paddle answered differently, not
 * because we branched.
 *
 * A non-component `.ts` module (Vite Fast Refresh rule) and a TanStack Query
 * wrapper rather than a `useEffect` (repo data-layer rule).
 *
 * ## One call per page, never one per card
 * Every purchasable price id goes into a SINGLE batched `PricePreview`. The
 * round-trip saving is the small half; the load-bearing half is that one call
 * yields ONE currency decision for the whole grid. Per-card calls could resolve
 * differently mid-render and paint a grid quoting two currencies.
 *
 * ## Soft fallback, always
 * A rejection (ad blocker on `cdn.paddle.com`, Paddle outage), an empty
 * `lineItems` array (placeholder `pri_REPLACE_ME_*` ids in an environment), or a
 * missing `currencyCode` all resolve to `isLocalized: false` with an empty map —
 * callers then render the USD catalog. The pricing page is never empty or broken
 * because localization failed.
 */

/** One plan's Paddle-resolved money, for its whole BILLING TERM (not per month). */
export type LocalizedPrice = {
  /** List amount before discount and before tax, MAJOR units. */
  subtotal: number;
  /** Discount applied for the term, MAJOR units. `0` unless a `discountId` was sent. */
  discount: number;
  /**
   * What is actually charged for the term, tax-EXCLUSIVE (`total − tax`), MAJOR
   * units. Tax is subtracted so Indian GST / EU VAT doesn't inflate the sticker
   * price, and so this matches the tax-exclusive basis the backend's
   * `recurringAmount` uses — the two surfaces must never quote different numbers.
   */
  amount: number;
  /** ISO 4217 code Paddle resolved for this visitor. */
  currency: string;
};

/** The resolved preview for a batch of price ids. */
export type PricePreview = {
  byPriceId: ReadonlyMap<string, LocalizedPrice>;
  /** The one currency Paddle picked for this visitor, or null when unresolved. */
  currencyCode: string | null;
  /** Paddle's resolved country. Diagnostics only — nothing gates on it. */
  countryCode: string | null;
  /** `false` → callers must render the USD catalog. */
  isLocalized: boolean;
};

export type PricePreviewState = PricePreview & {
  /**
   * The preview hasn't settled (or never runs, because Paddle is unconfigured or
   * there are no price ids). Callers must not draw the USD-fallback notice yet —
   * `isLocalized` is `false` during loading too, and flashing "Prices shown in
   * USD" before the answer arrives would be a lie half the time.
   */
  isPending: boolean;
};

/** Quantity every preview line asks for — plans are per-org, never per-seat. */
const PREVIEW_QUANTITY = 1;

/** Minor-unit digits assumed when `Intl` doesn't recognise the currency code. */
const DEFAULT_MINOR_UNIT_DIGITS = 2;

/** Quiet line under the grid when localization failed but Paddle IS configured. */
export const USD_FALLBACK_NOTICE = 'Prices shown in USD.';

const EMPTY_PRICE_PREVIEW: PricePreview = {
  byPriceId: new Map(),
  currencyCode: null,
  countryCode: null,
  isLocalized: false,
};

/**
 * How many minor units make one major unit of `currency`.
 *
 * DERIVED from the platform rather than a maintained zero-decimal set: JPY, KRW,
 * VND, CLP, ISK and friends have no minor unit at all, so a hardcoded `/ 100`
 * would render `¥4900` as `¥49`. An unknown code falls back to 2 digits, which
 * is what the overwhelming majority of ISO 4217 uses.
 */
export function minorUnitDivisor(currency: string): number {
  try {
    const { maximumFractionDigits } = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).resolvedOptions();
    // Typed optional by `lib.es5`; a currency-style formatter always resolves it.
    return 10 ** (maximumFractionDigits ?? DEFAULT_MINOR_UNIT_DIGITS);
  } catch {
    return 10 ** DEFAULT_MINOR_UNIT_DIGITS;
  }
}

/**
 * Paddle's `totals` are MINOR-UNIT STRINGS. Every subtraction happens on those
 * integers and the division to major units happens LAST — dividing first and
 * subtracting after loses precision on the float (e.g. `84244.5/100 − 0/100`).
 * Returns null when a field isn't a number, so one malformed line item drops
 * itself instead of poisoning the map with `NaN`.
 */
function toLocalizedPrice(totals: Totals, currency: string): LocalizedPrice | null {
  const subtotal = Number(totals.subtotal);
  const discount = Number(totals.discount);
  const tax = Number(totals.tax);
  const total = Number(totals.total);
  if (
    !Number.isFinite(subtotal) ||
    !Number.isFinite(discount) ||
    !Number.isFinite(tax) ||
    !Number.isFinite(total)
  ) {
    return null;
  }
  const divisor = minorUnitDivisor(currency);
  return {
    subtotal: subtotal / divisor,
    discount: discount / divisor,
    amount: (total - tax) / divisor,
    currency,
  };
}

/** Map a raw `PricePreview` response onto our shape. Exported for its own test. */
export function toPricePreview(response: PricePreviewResponse): PricePreview {
  const data = response.data;
  // Captured BEFORE any line-item branching so it survives a preview that
  // returns no usable items — that is exactly the case worth diagnosing.
  const countryCode = data.address?.countryCode ?? null;
  const currencyCode = data.currencyCode ?? null;
  const byPriceId = new Map<string, LocalizedPrice>();
  if (currencyCode) {
    for (const lineItem of data.details?.lineItems ?? []) {
      // Joined back by price id — the response's line-item ORDER is not a
      // contract, and a plan whose id Paddle didn't recognise is simply absent.
      const priceId = lineItem.price?.id;
      if (!priceId) continue;
      const price = toLocalizedPrice(lineItem.totals, currencyCode);
      if (price) byPriceId.set(priceId, price);
    }
  }
  return {
    byPriceId,
    currencyCode,
    countryCode,
    // A currency with no usable line item can't price anything, so it is not a
    // localized render — it is the USD-catalog fallback with a country attached.
    isLocalized: currencyCode != null && byPriceId.size > 0,
  };
}

async function runPricePreview(
  priceIds: readonly string[],
  customerId: string | null,
  discountId: string | null,
): Promise<PricePreview> {
  const paddle = await getPaddle();
  // Unconfigured environment (no client token) — the query is already disabled
  // for that case; this is the belt-and-braces path.
  if (!paddle) return EMPTY_PRICE_PREVIEW;
  const response = await paddle.PricePreview({
    items: priceIds.map((priceId) => ({ priceId, quantity: PREVIEW_QUANTITY })),
    // Sent ONLY when non-null. A stale or wrong customer id makes Paddle answer
    // with that customer's LOCKED currency, which can contradict where the
    // visitor actually is.
    ...(customerId ? { customerId } : {}),
    ...(discountId ? { discountId } : {}),
  });
  return toPricePreview(response);
}

/**
 * The shared query. Not cached: `staleTime: 0` re-asks Paddle on every mount and
 * `gcTime: 0` drops the answer as soon as nothing renders it, so a price is
 * never served from an earlier visit. `retry: false` because a blocked CDN fails
 * the same way three times, `refetchOnWindowFocus: false` because a currency must not change
 * under the user mid-decision, and `silentError` because the caller's answer to
 * a failure is the USD grid plus a quiet notice — not a red toast.
 */
function usePricePreviewQuery(
  priceIds: readonly string[],
  customerId: string | null,
  discountId: string | null,
): PricePreviewState {
  const enabled = isPaddleConfigured() && priceIds.length > 0;
  const query = useQuery<PricePreview>({
    queryKey: billingKeys.pricePreview(priceIds, customerId, discountId),
    queryFn: () => runPricePreview(priceIds, customerId, discountId),
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    meta: { silentError: true },
  });
  return { ...(query.data ?? EMPTY_PRICE_PREVIEW), isPending: enabled && !query.isFetched };
}

/** Every purchasable checkout price id in the catalog, deduped and SORTED so a
 * reordered catalog can't fork the query cache into two entries for one answer. */
function checkoutPriceIds(plans: BillingPlan[]): string[] {
  const ids = new Set<string>();
  for (const plan of plans) {
    if (isPurchasable(plan) && plan.paddleCheckoutPriceId) ids.add(plan.paddleCheckoutPriceId);
  }
  return [...ids].sort();
}

/**
 * One batched preview for a whole plan grid.
 *
 * `customerId` comes from `GET /billing/subscription`'s `paddleCustomerId`.
 * Callers should hold `plans` back (pass `[]`) until the subscription read has
 * settled — otherwise the key changes when it lands and the grid re-previews,
 * which can visibly re-price the page.
 */
export function usePricePreview(
  plans: BillingPlan[],
  customerId: string | null,
): PricePreviewState {
  const priceIds = useMemo(() => checkoutPriceIds(plans), [plans]);
  return usePricePreviewQuery(priceIds, customerId, null);
}

/**
 * The checkout dialog's single-price re-preview WITH a validated discount.
 *
 * `POST /billing/checkout/validate-code` stays the AUTHORIZATION check (may this
 * customer use this code on this plan); this is the DISPLAY source, so the list
 * price, the discount and the total all come from one Paddle response in one
 * currency. Dormant until both a price id and a discount id exist.
 */
export function useDiscountedPricePreview(params: {
  priceId: string | null;
  customerId: string | null;
  discountId: string | null;
}): PricePreviewState {
  const { priceId, customerId, discountId } = params;
  const priceIds = useMemo(
    () => (priceId && discountId ? [priceId] : []),
    [priceId, discountId],
  );
  return usePricePreviewQuery(priceIds, customerId, discountId);
}

/** This plan's localized price, or null when Paddle returned nothing for it. */
export function localizedPriceFor(
  byPriceId: ReadonlyMap<string, LocalizedPrice>,
  plan: BillingPlan | null | undefined,
): LocalizedPrice | null {
  const priceId = plan?.paddleCheckoutPriceId;
  return priceId ? (byPriceId.get(priceId) ?? null) : null;
}

/**
 * Whether to draw the "Prices shown in USD" line under the grid.
 *
 * Deliberately NOT shown when Paddle is simply unconfigured (local dev without a
 * client token): that is not a failure state, it is an environment with no
 * checkout at all, and labelling it would train everyone to ignore the notice.
 */
export function shouldNotifyUsdFallback(state: PricePreviewState): boolean {
  return isPaddleConfigured() && !state.isPending && !state.isLocalized;
}
